// src/client/renderer/character/head-swap.ts
//
// Head-swap system for SmashParty character rig (Plan 13, Task 2.1).
//
// Idempotent: a second call disposes the prior head before attaching the new one.
// Graceful-degrade per Decision 2 / Plan 13 preamble: when ANY of four trigger conditions
// fires (requested id in PLAN_13_GRACEFUL_DEGRADE_HEADS, manifest entry missing,
// manifest placeholder:true, or headGltfScene === null), this function attaches
// the fallbackGltfScene (caller-loaded 'horse' GLB) and emits exactly ONE console.warn.
// The fallback path does NOT recursively trigger the warn.
//
// Prior-head tracking: Option A — `rig._headMesh` on CharacterRig interface.
// Chosen for simplicity; the `_` prefix signals "internal — do not use externally."

import type { CharacterRig, HeadId } from './types';
import { PLAN_13_GRACEFUL_DEGRADE_HEADS, PLAN_13_HEAD_FALLBACK_TARGET } from './asset-inventory';
import { findFirstMesh } from './_internals';
import { HEAD_TUNING } from './head-tuning';
import { disposeEyeMeshes } from './eye-sprite';
import * as THREE from 'three';

interface HeadSurfaceBinding {
  readonly skinnedMesh: THREE.SkinnedMesh;
  readonly orientationSocket: THREE.Object3D | null;
  readonly parent: THREE.Object3D;
  readonly sampleIndices: readonly number[];
  readonly restContactParent: THREE.Vector3;
  readonly restOrientationInverse: THREE.Quaternion;
}

const headSurfaceBindings = new WeakMap<CharacterRig, HeadSurfaceBinding>();
const HEAD_CONTACT_MAX_SAMPLES = 24;
const HEAD_CONTACT_TOP_BAND_FRACTION = 0.16;
const HEAD_CONTACT_CENTRAL_WEIGHT = 0.36;
const HEAD_CONTACT_STRICT_HEAD_BONE_WEIGHT = 0.0001;
const HEAD_CONTACT_MAX_HEAD_BONE_WEIGHT = 0.25;
const HEAD_CONTACT_HEAD_BONE_WEIGHT_PENALTY = 4;
const tempHeadContactWorld = new THREE.Vector3();
const tempHeadContactSample = new THREE.Vector3();
const tempHeadParentQuaternion = new THREE.Quaternion();
const tempHeadSocketQuaternion = new THREE.Quaternion();
const tempHeadSocketRelativeQuaternion = new THREE.Quaternion();

/**
 * Swap or attach a head mesh to the character rig.
 *
 * Trigger order for graceful-degrade (Decision 2):
 *   (a) requestedId in PLAN_13_GRACEFUL_DEGRADE_HEADS
 *   (b) manifestEntry === undefined
 *   (c) manifestEntry.placeholder === true
 *   (d) headGltfScene === null (caller signals load failure)
 *
 * Any of (a-d) → fallback to 'horse' GLB and emit ONE console.warn.
 * Normal path (none of a-d) → clone the full GLB scene Group and attach.
 *
 * Why we clone the full Group (not just the mesh geometry+material):
 *   GLBs may author the head with non-identity scale on a parent node
 *   (e.g. horse: scale 0.789, alien: scale 0.421). Extracting only the mesh
 *   loses those parent transforms — the head renders at the wrong size AND
 *   the bbox math uses unscaled geometry, causing floating heads.
 *   Cloning the whole Group preserves the GLB's intended transform hierarchy.
 *   THREE.Box3.setFromObject then walks matrixWorld through every nested node,
 *   giving the actual world-space bbox regardless of GLB authoring convention.
 */
export function swapHead(args: {
  rig: CharacterRig;
  headId: HeadId;
  headGltfScene: THREE.Group | null;
  fallbackGltfScene?: THREE.Group;
  manifestEntry?: { placeholder: boolean };
}): void {
  const { rig, headId, headGltfScene, fallbackGltfScene, manifestEntry } = args;

  // ── 1. Determine whether graceful-degrade fires ─────────────────────────────
  const degradeByName = (PLAN_13_GRACEFUL_DEGRADE_HEADS as readonly string[]).includes(headId);
  const degradeByAbsentManifest = manifestEntry === undefined;
  const degradeByPlaceholder = manifestEntry !== undefined && manifestEntry.placeholder === true;
  const degradeByLoadFailure = headGltfScene === null;

  const shouldDegrade =
    degradeByName || degradeByAbsentManifest || degradeByPlaceholder || degradeByLoadFailure;

  let meshSource: THREE.Group;

  if (shouldDegrade) {
    // Emit exactly ONE warn with the canonical format.
    console.warn(
      `[Plan 13] head id "${headId}" not available; falling back to "${PLAN_13_HEAD_FALLBACK_TARGET}"`,
    );

    if (fallbackGltfScene === undefined) {
      throw new Error(
        `[swapHead] graceful-degrade fired for "${headId}" but no fallbackGltfScene was provided. ` +
          `Caller contract violation: fallbackGltfScene is required when degrade conditions are met.`,
      );
    }

    meshSource = fallbackGltfScene;
  } else {
    // headGltfScene is non-null here because degradeByLoadFailure === false.
    // The TypeScript type knows it may be null, but we've already handled that case.
    meshSource = headGltfScene as THREE.Group;
  }

  // ── 2. Dispose prior head if a previous swap has been done (idempotent) ─────
  disposeEyeMeshes(rig);
  if (rig._headMesh != null) {
    disposeHead(rig);
  } else if (rig._headRoot != null) {
    disposeHead(rig);
  }

  // ── 3. Clone the ENTIRE source scene Group, then reset the cloned root ────────
  // Nested transforms baked by the GLB exporter stay in the subtree. The cloned
  // root starts from identity so HEAD_TUNING is the single source of runtime pose.
  const headGroup = meshSource.clone(true); // deep clone (children too)
  cloneOwnedMeshResources(headGroup);

  // Reset scale to identity so head matches body's render scale.
  headGroup.scale.set(1, 1, 1);
  // Also reset position & rotation to defaults; we'll set position.y below.
  headGroup.position.set(0, 0, 0);
  headGroup.rotation.set(0, 0, 0);

  // Force matrix world update so Box3.setFromObject sees the updated transforms.
  headGroup.updateMatrixWorld(true);

  const visibleMesh = findFirstMesh(headGroup);
  if (visibleMesh === null) {
    throw new Error(
      `[swapHead] cloned head group for "${headId}" has no Mesh children. ` +
        `GLB likely empty or malformed.`,
    );
  }

  // ── 7. Attach the Group ──────────────────────────────────────────────────────
  const visualRoot = rig.visualRoot ?? rig.root;
  const headMount = new THREE.Group();
  headMount.name = 'character-head-mount';
  headMount.userData.characterHeadMount = true;
  headGroup.name = headGroup.name || `character-head:${headId}`;
  headGroup.userData.characterHeadVisualRoot = true;
  positionHeadMountAndVisual(headMount, headGroup, rig, headId);
  headMount.add(headGroup);
  visualRoot.add(headMount);
  rig._headRoot = headMount;

  // ── 8. Track on rig._headMesh slot ────────────────────────────────────────────
  // rig._headMesh is typed as `THREE.Mesh | null | undefined`. We need the
  // outline binding + dispose chain to work with the Group too. Cheapest path:
  // find the FIRST Mesh in the cloned group via findFirstMesh and store THAT
  // as _headMesh (it's the visual mesh; the Group is its parent in the rig.root
  // hierarchy). Outline binding currently reads _headMesh's geometry — keep that
  // shape so outline still works. Eye-sprite reads _headMesh's geometry/bbox
  // for face anchoring — same.
  rig._headMesh = visibleMesh;
  bindHeadSurface(rig, headMount, visualRoot);
  updateHeadSocketBinding(rig);
}

function bindHeadSurface(
  rig: CharacterRig,
  headMount: THREE.Object3D,
  parent: THREE.Object3D,
): void {
  const skinnedMesh = rig.bodySkinnedMesh ?? null;
  if (skinnedMesh == null) {
    headSurfaceBindings.delete(rig);
    return;
  }
  if (
    skinnedMesh.skeleton == null ||
    skinnedMesh.geometry.getAttribute('skinIndex') == null ||
    skinnedMesh.geometry.getAttribute('skinWeight') == null
  ) {
    headSurfaceBindings.delete(rig);
    return;
  }

  const sampleIndices = selectHeadContactVertexIndices(skinnedMesh);
  if (sampleIndices.length === 0) {
    headSurfaceBindings.delete(rig);
    return;
  }

  const orientationSocket =
    rig.resolveSocket?.('head') ??
    rig.resolveSocket?.('neck') ??
    rig.resolveSocket?.('cape') ??
    null;
  rig.mixer?.update(0);
  rig.root.updateWorldMatrix(true, true);
  rig.modelRoot?.updateMatrixWorld(true);
  skinnedMesh.skeleton.update();
  skinnedMesh.updateWorldMatrix(true, false);
  orientationSocket?.updateWorldMatrix(true, false);
  parent.updateWorldMatrix(true, false);
  const restContactParent = computeHeadContactParentPosition(skinnedMesh, parent, sampleIndices);
  if (restContactParent == null) {
    headSurfaceBindings.delete(rig);
    return;
  }
  const visualHeadRoot = headMount.children[0] ?? null;
  if (visualHeadRoot != null) visualHeadRoot.position.sub(restContactParent);
  headMount.position.copy(restContactParent);

  const restOrientationInverse = getRelativeSocketQuaternion(parent, orientationSocket)
    .clone()
    .invert();
  headSurfaceBindings.set(rig, {
    skinnedMesh,
    orientationSocket,
    parent,
    sampleIndices,
    restContactParent,
    restOrientationInverse,
  });
  updateHeadSurfaceBinding(rig, headMount);
}

export function updateHeadSocketBinding(rig: CharacterRig): void {
  updateHeadSurfaceBinding(rig, rig._headRoot ?? null);
}

function updateHeadSurfaceBinding(rig: CharacterRig, headRoot: THREE.Object3D | null): void {
  const binding = headSurfaceBindings.get(rig);
  if (binding == null || headRoot == null || headRoot.parent !== binding.parent) return;

  rig.root.updateWorldMatrix(true, true);
  rig.modelRoot?.updateMatrixWorld(true);
  binding.skinnedMesh.skeleton.update();
  binding.skinnedMesh.updateWorldMatrix(true, false);
  binding.orientationSocket?.updateWorldMatrix(true, false);
  binding.parent.updateWorldMatrix(true, false);

  const contactParent = computeHeadContactParentPosition(
    binding.skinnedMesh,
    binding.parent,
    binding.sampleIndices,
  );
  if (contactParent == null) return;
  headRoot.position.copy(contactParent);

  if (binding.orientationSocket != null) {
    headRoot.quaternion
      .copy(getRelativeSocketQuaternion(binding.parent, binding.orientationSocket))
      .multiply(binding.restOrientationInverse);
  } else {
    headRoot.quaternion.identity();
  }
  headRoot.scale.set(1, 1, 1);
  headRoot.updateMatrixWorld(true);
}

function computeHeadContactParentPosition(
  skinnedMesh: THREE.SkinnedMesh,
  parent: THREE.Object3D,
  sampleIndices: readonly number[],
): THREE.Vector3 | null {
  const position = skinnedMesh.geometry.getAttribute('position') as
    | THREE.BufferAttribute
    | undefined;
  if (!(position instanceof THREE.BufferAttribute) || sampleIndices.length === 0) return null;

  tempHeadContactWorld.set(0, 0, 0);
  for (const index of sampleIndices) {
    tempHeadContactSample.fromBufferAttribute(position, index);
    skinnedMesh.applyBoneTransform(index, tempHeadContactSample);
    skinnedMesh.localToWorld(tempHeadContactSample);
    tempHeadContactWorld.add(tempHeadContactSample);
  }
  tempHeadContactWorld.multiplyScalar(1 / sampleIndices.length);
  return parent.worldToLocal(tempHeadContactWorld.clone());
}

function getRelativeSocketQuaternion(
  parent: THREE.Object3D,
  socket: THREE.Object3D | null,
): THREE.Quaternion {
  if (socket == null) return tempHeadSocketRelativeQuaternion.identity();
  parent.getWorldQuaternion(tempHeadParentQuaternion).invert();
  socket.getWorldQuaternion(tempHeadSocketQuaternion);
  return tempHeadSocketRelativeQuaternion.multiplyQuaternions(
    tempHeadParentQuaternion,
    tempHeadSocketQuaternion,
  );
}

function selectHeadContactVertexIndices(skinnedMesh: THREE.SkinnedMesh): number[] {
  const geometry = skinnedMesh.geometry;
  const position = geometry.getAttribute('position');
  if (!(position instanceof THREE.BufferAttribute)) return [];

  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (box == null) return [];

  const height = Math.max(box.max.y - box.min.y, 0.0001);
  const halfWidth = Math.max((box.max.x - box.min.x) * 0.5, 0.0001);
  const halfDepth = Math.max((box.max.z - box.min.z) * 0.5, 0.0001);
  const centerX = (box.min.x + box.max.x) * 0.5;
  const centerZ = (box.min.z + box.max.z) * 0.5;
  const topBandY = box.max.y - height * HEAD_CONTACT_TOP_BAND_FRACTION;

  const skinIndex = geometry.getAttribute('skinIndex') as THREE.BufferAttribute | undefined;
  const skinWeight = geometry.getAttribute('skinWeight') as THREE.BufferAttribute | undefined;
  const headBoneIndices = new Set<number>();
  for (const [index, bone] of skinnedMesh.skeleton.bones.entries()) {
    if (bone.name === 'Head' || bone.name === 'headfront') headBoneIndices.add(index);
  }

  const candidates: { index: number; score: number; headWeight: number }[] = [];
  const strictBodyOwnedCandidates: { index: number; score: number; headWeight: number }[] = [];
  const bodyOwnedCandidates: { index: number; score: number; headWeight: number }[] = [];
  for (let index = 0; index < position.count; index += 1) {
    const y = position.getY(index);
    const x = position.getX(index);
    const z = position.getZ(index);
    const normalizedTopDistance = (box.max.y - y) / height;
    const normalizedCenterDistance = Math.hypot(
      (x - centerX) / halfWidth,
      (z - centerZ) / halfDepth,
    );
    const headWeight =
      skinIndex != null && skinWeight != null
        ? skinWeightForBoneSet(index, skinIndex, skinWeight, headBoneIndices)
        : 0;
    const candidate = {
      index,
      headWeight,
      score:
        normalizedTopDistance +
        normalizedCenterDistance * HEAD_CONTACT_CENTRAL_WEIGHT +
        headWeight * HEAD_CONTACT_HEAD_BONE_WEIGHT_PENALTY,
    };
    if (headWeight <= HEAD_CONTACT_STRICT_HEAD_BONE_WEIGHT) {
      strictBodyOwnedCandidates.push(candidate);
    }
    if (headWeight <= HEAD_CONTACT_MAX_HEAD_BONE_WEIGHT) bodyOwnedCandidates.push(candidate);
    if (y >= topBandY) candidates.push(candidate);
  }

  if (candidates.length === 0) {
    for (let index = 0; index < position.count; index += 1) {
      const y = position.getY(index);
      const headWeight =
        skinIndex != null && skinWeight != null
          ? skinWeightForBoneSet(index, skinIndex, skinWeight, headBoneIndices)
          : 0;
      candidates.push({
        index,
        headWeight,
        score: (box.max.y - y) / height + headWeight * HEAD_CONTACT_HEAD_BONE_WEIGHT_PENALTY,
      });
    }
  }

  const strictBodyOwnedTopCandidates = candidates.filter(
    (candidate) => candidate.headWeight <= HEAD_CONTACT_STRICT_HEAD_BONE_WEIGHT,
  );
  const bodyOwnedTopCandidates = candidates.filter(
    (candidate) => candidate.headWeight <= HEAD_CONTACT_MAX_HEAD_BONE_WEIGHT,
  );
  let ranked = strictBodyOwnedTopCandidates;
  if (ranked.length === 0) ranked = bodyOwnedTopCandidates;
  if (ranked.length === 0) ranked = strictBodyOwnedCandidates;
  if (ranked.length === 0) ranked = bodyOwnedCandidates;
  if (ranked.length === 0) ranked = candidates;
  ranked.sort((a, b) => a.score - b.score);
  return ranked.slice(0, HEAD_CONTACT_MAX_SAMPLES).map((candidate) => candidate.index);
}

function skinWeightForBoneSet(
  vertexIndex: number,
  skinIndex: THREE.BufferAttribute,
  skinWeight: THREE.BufferAttribute,
  boneIndices: ReadonlySet<number>,
): number {
  if (boneIndices.size === 0) return 0;
  const indices = [
    skinIndex.getX(vertexIndex),
    skinIndex.getY(vertexIndex),
    skinIndex.getZ(vertexIndex),
    skinIndex.getW(vertexIndex),
  ];
  const weights = [
    skinWeight.getX(vertexIndex),
    skinWeight.getY(vertexIndex),
    skinWeight.getZ(vertexIndex),
    skinWeight.getW(vertexIndex),
  ];
  let total = 0;
  for (let index = 0; index < indices.length; index += 1) {
    if (boneIndices.has(indices[index] ?? -1)) total += weights[index] ?? 0;
  }
  return total;
}

function cloneOwnedMeshResources(root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    obj.geometry = obj.geometry.clone();
    const material = obj.material;
    obj.material = Array.isArray(material)
      ? material.map((mat) => mat.clone())
      : (material as THREE.Material).clone();
  });
}

/**
 * Dispose the head mesh slot. Idempotent — no-op when `_headMesh` is null/undefined.
 *
 * Called by the assembler's `rig.dispose()` to release head GPU resources.
 * Co-located with `swapHead` so the head's complete lifecycle (attach + dispose)
 * lives in one module — assembler.ts doesn't need to know the slot's internals.
 *
 * Texture lifecycle: the cloned group's material texture maps (`.map`, `.normalMap`,
 * etc.) are NOT disposed — they are shared with the GLTFLoader cache (see
 * `disposeMesh` in `_internals.ts` for the full rationale).
 *
 * `_headRoot` is stored explicitly so disposal never needs to inspect body sockets.
 */
export function disposeHead(rig: CharacterRig): void {
  disposeEyeMeshes(rig);
  headSurfaceBindings.delete(rig);
  const topAncestor = rig._headRoot ?? rig._headMesh;
  if (topAncestor == null) return;
  topAncestor.removeFromParent();
  // Dispose all geometries + materials in the subtree
  topAncestor.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.geometry?.dispose?.();
      const m = obj.material;
      if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
      else if (m != null) (m as THREE.Material).dispose();
    }
  });
  rig._headRoot = null;
  rig._headMesh = null;
}

function positionHeadMountAndVisual(
  headMount: THREE.Group,
  headGroup: THREE.Group,
  rig: CharacterRig,
  headId: HeadId,
): void {
  const tuning = HEAD_TUNING[headId];

  rig.bodyMesh.geometry.computeBoundingBox();
  const bodyMaxY = rig.bodyMesh.geometry.boundingBox?.max.y ?? 1.0;

  headMount.position.set(0, 0, 0);
  headMount.rotation.set(0, 0, 0);
  headMount.scale.set(1, 1, 1);

  if (tuning != null) {
    headGroup.scale.set(tuning.scale, tuning.scale, tuning.scale);
    headGroup.position.set(0, 0, 0);
    headGroup.rotation.set(tuning.rotationX, tuning.rotationY, tuning.rotationZ);
    headGroup.updateMatrixWorld(true);
    const scaledBB = new THREE.Box3().setFromObject(headGroup);
    headGroup.position.set(
      tuning.xOffset,
      bodyMaxY - scaledBB.min.y + tuning.yOffset,
      tuning.zOffset,
    );
    return;
  }

  const worldBB = new THREE.Box3().setFromObject(headGroup);
  headGroup.position.y = bodyMaxY - worldBB.min.y;
}
